import getFirstGridElement from 'editor/src/store/design/selector/getFirstGridElement';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';
import { isEmbedded } from 'editor/src/store/watchers/useDisplayMode';

import { isSameCountries } from 'editor/src/util/calendar/calendarCountries';

import requestCalendarCountriesOperation from './requestCalendarCountriesOperation';

/**
 * Gives a design that names no countries yet the host's country, whose holidays it got before it
 * could name any. In the customer's editor with the merchant's "country of the customer" switch on,
 * the customer's country, until they pick their own.
 * Safe to run on every design change: it acts once, then the design names its countries.
 */
const getCountriesToApply = (state: RootState): string[] | null => {
  const supported = state.hostSettings.calendarEventsCountries;
  if (!supported.length) {
    return null;
  }
  if (!getFirstGridElement(state)) {
    return null;
  }

  const calendar = state.design.designData?.calendar;

  const hostCountry = state.hostSettings.calendarEventsDefaultCountry;
  const defaultCountry = hostCountry && supported.includes(hostCountry) ? hostCountry : null;

  if (isEmbedded) {
    // Otherwise the customer keeps the merchant's holidays.
    return calendar?.autoApplyPublicHolidays &&
      !calendar.countriesChosenByCustomer &&
      defaultCountry &&
      !isSameCountries(calendar.countries, [defaultCountry])
      ? [defaultCountry]
      : null;
  }

  return !calendar?.countries && defaultCountry ? [defaultCountry] : null;
};

const syncCalendarCountriesOperation = () => (dispatch: ThunkDispatch, getState: () => RootState) => {
  const countries = getCountriesToApply(getState());
  if (countries) {
    dispatch(requestCalendarCountriesOperation(countries));
  }
};

export default syncCalendarCountriesOperation;
